/**
 * Reports, built on the screen from the rows `GET /reports` returns (see
 * reports.service.js): one per ledger cycle, one per internal fix. Every
 * total here is a sum over rows the reader can see in a table, so the
 * overview and the tables can never disagree.
 */

const DAY = 86_400_000
const t = (iso) => (iso ? new Date(iso).getTime() : null)

/** `YYYY-MM-DD` in IST — the day a person in the office would call it. */
const istDay = (iso) => (iso ? new Date(t(iso) + 5.5 * 3_600_000).toISOString().slice(0, 10) : null)

// ── Date range ──────────────────────────────────────────────────────────────

const todayIst = () => istDay(new Date().toISOString())
const shift = (days) => istDay(new Date(Date.now() + days * DAY).toISOString())
const fyStartOf = (isoDay) => {
  const [y, m] = isoDay.split('-').map(Number)
  return `${m >= 4 ? y : y - 1}-04-01`
}

export const RANGE_PRESETS = [
  { value: 'all', label: 'All time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'month', label: 'This month' },
  { value: 'fy', label: 'This financial year' },
  { value: 'custom', label: 'Custom range…' },
]

/** `{ from, to }` (inclusive `YYYY-MM-DD`, either may be '') for a preset. */
export function presetRange(preset, custom = { from: '', to: '' }) {
  const today = todayIst()
  switch (preset) {
    case '7d':
      return { from: shift(-6), to: today }
    case '30d':
      return { from: shift(-29), to: today }
    case '90d':
      return { from: shift(-89), to: today }
    case 'month':
      return { from: `${today.slice(0, 7)}-01`, to: today }
    case 'fy':
      return { from: fyStartOf(today), to: today }
    case 'custom':
      return { from: custom.from || '', to: custom.to || '' }
    default:
      return { from: '', to: '' }
  }
}

/** Does the span [start, end ?? today] touch the range? Empty ends are open. */
function overlaps(startIso, endIso, { from, to }) {
  const s = istDay(startIso) ?? '0000-00-00'
  const e = endIso ? istDay(endIso) : todayIst()
  if (from && e < from) return false
  if (to && s > to) return false
  return true
}

/** A cycle is in range if it was active at any point in it — opened before the end, not finished before the start. */
export const ledgerInRange = (l, range) =>
  overlaps(l.openedAt, l.stage === 'clean' ? l.lastRunAt ?? l.receivedAt : null, range)

/** A fix is in range if it was open at any point in it. */
export const itemInRange = (i, range) => overlaps(i.createdAt, i.confirmedAt, range)

// ── Helpers ─────────────────────────────────────────────────────────────────

export const sum = (list, f) => list.reduce((a, x) => a + (Number(f(x)) || 0), 0)
const pct = (n, d) => (d ? Math.round((n / d) * 100) : null)
function median(nums) {
  const v = nums.filter((n) => Number.isFinite(n)).sort((a, b) => a - b)
  if (!v.length) return null
  const m = Math.floor(v.length / 2)
  return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2
}
const avg = (nums) => {
  const v = nums.filter((n) => Number.isFinite(n))
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null
}

/** Working time so far, the running stretch included. */
export function workedNow(i, now = Date.now()) {
  const banked = Number(i.workedMs) || 0
  return i.status === 'in_progress' && i.lastResumedAt ? banked + Math.max(0, now - t(i.lastResumedAt)) : banked
}

export const daysToReceive = (l) => (l.receivedAt && l.firstAskedAt ? (t(l.receivedAt) - t(l.firstAskedAt)) / DAY : null)

const NEEDS_US = new Set(['THEY_REPLIED', 'REDIRECTED'])
const BAD_DOC = new Set(['DOC_WRONG_PERIOD', 'DOC_UNREADABLE', 'DOC_NOT_STATEMENT', 'DOC_REJECTED'])
const CLIENT_FIX = new Set(['needs_client_fix', 'needs_both_fix'])
const OUR_FIX = new Set(['needs_our_fix', 'needs_both_fix'])

// ── Overview ────────────────────────────────────────────────────────────────

export function overview(ledgers, items, now = Date.now()) {
  const asked = ledgers.filter((l) => l.firstAskedAt || l.waOut + l.emailOut > 0)
  const received = ledgers.filter((l) => l.receivedAt)
  // What got through — a failed send wasn't a message the client saw.
  const messages = sum(ledgers, (l) => l.waOut + l.emailOut + l.callOut - l.failed)
  const reconciled = ledgers.filter((l) => l.runsSucceeded > 0)
  const clientFix = ledgers.filter((l) => CLIENT_FIX.has(l.stage))
  const open = items.filter((i) => i.status !== 'confirmed')
  const done = items.filter((i) => i.status === 'confirmed')

  return {
    chase: {
      cycles: ledgers.length,
      clientsAsked: new Set(asked.map((l) => l.clientCode)).size,
      messages,
      waOut: sum(ledgers, (l) => l.waOut),
      emailOut: sum(ledgers, (l) => l.emailOut),
      deliveredPct: pct(sum(ledgers, (l) => l.delivered), sum(ledgers, (l) => l.waOut + l.emailOut)),
      readPct: pct(sum(ledgers, (l) => l.read), sum(ledgers, (l) => l.waOut)),
      failed: sum(ledgers, (l) => l.failed),
      replied: ledgers.filter((l) => l.inbound > 0).length,
      replyRate: pct(ledgers.filter((l) => l.inbound > 0).length, asked.length),
      received: received.length,
      receivedRate: pct(received.length, asked.length),
      medianDaysToReceive: median(received.map(daysToReceive)),
      awaitingReply: ledgers.filter((l) => !l.receivedAt && l.conversationState === 'AWAITING_REPLY').length,
      needsUs: ledgers.filter((l) => !l.receivedAt && NEEDS_US.has(l.conversationState)).length,
      promised: ledgers.filter((l) => l.conversationState === 'PROMISED').length,
      promiseLate: ledgers.filter((l) => l.conversationState === 'PROMISE_LATE').length,
      disputed: ledgers.filter((l) => l.disputed).length,
      refused: ledgers.filter((l) => l.conversationState === 'REFUSED').length,
      badContact: ledgers.filter((l) => l.conversationState === 'BAD_CONTACT').length,
      badDocument: ledgers.filter((l) => BAD_DOC.has(l.conversationState)).length,
      sendFailing: ledgers.filter((l) => l.sendFailing).length,
      avgMessagesToReceive: avg(received.map((l) => l.waOut + l.emailOut + l.callOut - l.failed)),
    },
    reco: {
      waitingOnCollection: ledgers.filter((l) => l.stage === 'collecting').length,
      needsConfirming: ledgers.filter((l) => l.waitingOn === 'confirmation').length,
      ourSideMissing: ledgers.filter((l) => l.waitingOn === 'our_side').length,
      ready: ledgers.filter((l) => l.stage === 'ready').length,
      reconciling: ledgers.filter((l) => l.stage === 'reconciling').length,
      failed: ledgers.filter((l) => l.stage === 'failed').length,
      runs: sum(ledgers, (l) => l.runs),
      runsFailed: sum(ledgers, (l) => l.runsFailed),
      reconciled: reconciled.length,
      clean: ledgers.filter((l) => l.stage === 'clean').length,
      cleanRate: pct(ledgers.filter((l) => l.stage === 'clean').length, reconciled.length),
      clientFix: ledgers.filter((l) => l.stage === 'needs_client_fix').length,
      ourFix: ledgers.filter((l) => l.stage === 'needs_our_fix').length,
      bothFix: ledgers.filter((l) => l.stage === 'needs_both_fix').length,
      medianDaysReceiveToReco: median(reconciled.map((l) => (l.receivedAt && l.firstRunAt ? (t(l.firstRunAt) - t(l.receivedAt)) / DAY : null))),
    },
    postReco: {
      waitingOnClient: clientFix.length,
      needsPerson: ledgers.filter((l) => l.waitingOn === 'needs_person').length,
      oldestClientDays: clientFix.length ? Math.max(...clientFix.map((l) => (now - t(l.lastRunAt ?? l.receivedAt)) / DAY)) : null,
      waitingOnUs: ledgers.filter((l) => OUR_FIX.has(l.stage)).length,
      clientsWithOpenFixes: new Set(open.map((i) => i.clientCode)).size,
    },
    internal: {
      raised: items.length,
      open: open.length,
      notStarted: open.filter((i) => i.status === 'open').length,
      inProgress: open.filter((i) => i.status === 'in_progress').length,
      paused: open.filter((i) => i.status === 'paused').length,
      done: done.length,
      doneRate: pct(done.length, items.length),
      workedMs: sum(items, (i) => workedNow(i, now)),
      avgWorkMs: avg(done.map((i) => workedNow(i, now)).filter((ms) => ms > 0)),
      medianTurnaroundDays: median(done.map((i) => (t(i.confirmedAt) - t(i.createdAt)) / DAY)),
      oldestOpenDays: open.length ? Math.max(...open.map((i) => (now - t(i.createdAt)) / DAY)) : null,
      openValue: sum(open, (i) => i.amount),
      pauses: sum(items, (i) => i.pauses),
    },
    funnel: [
      { key: 'asked', label: 'Asked', n: asked.length },
      { key: 'replied', label: 'Replied', n: ledgers.filter((l) => l.inbound > 0 || l.receivedAt).length },
      { key: 'received', label: 'Statement received', n: received.length },
      { key: 'confirmed', label: 'Confirmed', n: ledgers.filter((l) => l.confirmedAt).length },
      { key: 'reconciled', label: 'Reconciled', n: reconciled.length },
      { key: 'clean', label: 'Clean', n: ledgers.filter((l) => l.stage === 'clean').length },
    ],
  }
}

// ── By client ───────────────────────────────────────────────────────────────

export function byClient(ledgers, items, now = Date.now()) {
  const groups = new Map()
  for (const l of ledgers) {
    const g = groups.get(l.clientCode) ?? { clientCode: l.clientCode, clientName: l.clientName, sample: l.sample, cycles: [] }
    g.cycles.push(l)
    groups.set(l.clientCode, g)
  }
  const fixesBy = new Map()
  for (const i of items) {
    const list = fixesBy.get(i.clientCode) ?? []
    list.push(i)
    fixesBy.set(i.clientCode, list)
  }
  return [...groups.values()].map((g) => {
    const latest = [...g.cycles].sort((a, b) => (t(b.openedAt) ?? 0) - (t(a.openedAt) ?? 0))[0]
    const fixes = fixesBy.get(g.clientCode) ?? []
    const receivedCycles = g.cycles.filter((c) => c.receivedAt)
    return {
      clientCode: g.clientCode,
      clientName: g.clientName,
      sample: g.sample,
      latest,
      cycles: g.cycles.length,
      firstAskedAt: g.cycles.map((c) => c.firstAskedAt).filter(Boolean).sort()[0] ?? null,
      messages: sum(g.cycles, (c) => c.waOut + c.emailOut + c.callOut - c.failed),
      waOut: sum(g.cycles, (c) => c.waOut),
      emailOut: sum(g.cycles, (c) => c.emailOut),
      failed: sum(g.cycles, (c) => c.failed),
      replies: sum(g.cycles, (c) => c.inbound),
      lastReplyAt: g.cycles.map((c) => c.lastReplyAt).filter(Boolean).sort().at(-1) ?? null,
      received: receivedCycles.length,
      avgDaysToReceive: avg(receivedCycles.map(daysToReceive)),
      conversationState: latest?.conversationState ?? null,
      stage: latest?.stage ?? 'collecting',
      runs: sum(g.cycles, (c) => c.runs),
      lastRunAt: g.cycles.map((c) => c.lastRunAt).filter(Boolean).sort().at(-1) ?? null,
      fixesOpen: fixes.filter((i) => i.status !== 'confirmed').length,
      fixesDone: fixes.filter((i) => i.status === 'confirmed').length,
      fixesWorkedMs: sum(fixes, (i) => workedNow(i, now)),
    }
  })
}

// ── By accountant ───────────────────────────────────────────────────────────

export function byAccountant(items, accountants, now = Date.now()) {
  const byEmail = new Map()
  const row = (email, a = null) => {
    const key = String(email ?? '').toLowerCase() || '(unassigned)'
    if (!byEmail.has(key)) {
      byEmail.set(key, { email: key, name: a?.name ?? (key === '(unassigned)' ? 'Unassigned' : key), category: a?.category ?? '', nudgeCount: a?.nudgeCount ?? 0, lastNudgeAt: a?.lastNudgeAt ?? null, items: [] })
    }
    return byEmail.get(key)
  }
  for (const a of accountants) row(a.email, a)
  for (const i of items) row(i.assignedAccountantEmail).items.push(i)

  return [...byEmail.values()].map((r) => {
    const open = r.items.filter((i) => i.status !== 'confirmed')
    const done = r.items.filter((i) => i.status === 'confirmed')
    return {
      ...r,
      total: r.items.length,
      open: open.length,
      notStarted: open.filter((i) => i.status === 'open').length,
      inProgress: open.filter((i) => i.status === 'in_progress').length,
      paused: open.filter((i) => i.status === 'paused').length,
      done: done.length,
      workedMs: sum(r.items, (i) => workedNow(i, now)),
      avgWorkMs: avg(done.map((i) => workedNow(i, now)).filter((ms) => ms > 0)),
      avgTurnaroundDays: avg(done.map((i) => (t(i.confirmedAt) - t(i.createdAt)) / DAY)),
      avgPickupDays: avg(r.items.filter((i) => i.startedAt).map((i) => (t(i.startedAt) - t(i.createdAt)) / DAY)),
      oldestOpenDays: open.length ? Math.max(...open.map((i) => (now - t(i.createdAt)) / DAY)) : null,
      openValue: sum(open, (i) => i.amount),
      pauses: sum(r.items, (i) => i.pauses),
      lastActivityAt: r.items.map((i) => i.lastActivityAt ?? i.confirmedAt ?? i.createdAt).filter(Boolean).sort().at(-1) ?? null,
    }
  })
}

// ── CSV ─────────────────────────────────────────────────────────────────────

/** `columns` = [{ label, csv: row => value }]. Quoted where it has to be. */
export function toCsv(rows, columns) {
  const cell = (v) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  return [columns.map((c) => cell(c.label)).join(','), ...rows.map((r) => columns.map((c) => cell(c.csv(r))).join(','))].join('\n')
}
